import { useLayoutEffect, useRef, useState } from "react";

/**
 * Is this element's text actually being clipped?
 *
 * Pairs with the `.li-dm-tt` hover tooltip in sections.scss, whose selector is
 * `.li-dm-tt[data-tt]:hover::after` — so the class can sit on a wrapper
 * permanently (it only adds `position: relative`) while `data-tt` is toggled on
 * real truncation. A short value then fires no redundant tooltip, which is the
 * whole point: a tooltip that repeats text already fully on screen is noise.
 *
 * TWO PLACEMENT RULES, both learned the hard way in AnthroOverviewCard:
 *   · the REF goes on the element that carries the ellipsis (the one with
 *     `overflow: hidden`), because that is the only node whose scrollWidth
 *     exceeds its clientWidth;
 *   · the CLASS goes on an ancestor WITHOUT `overflow: hidden`, or the
 *     `::after` is clipped by the very box that clipped the text.
 *
 * Re-measures on resize through ResizeObserver, so a column that narrows gains
 * its tooltip without a re-render — and the guard keeps jsdom (which has no
 * ResizeObserver) working in the unit tests.
 */
const useIsOverflowing = (text) => {
  const ref = useRef(null);
  const [overflowing, setOverflowing] = useState(false);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const check = () => setOverflowing(el.scrollWidth > el.clientWidth + 1);
    check();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [text]);
  return [ref, overflowing];
};

export default useIsOverflowing;
